import {
  Modal,
  Stack,
  Button,
  Group,
  TextInput,
  Textarea,
  Select,
  ScrollArea,
  Box,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCreateMacroRequest } from "../../../hooks/admin/useMacroRequests";
import { required, scrollToFirstError } from "../../../utils/formValidation";

interface RequestMacroFormValues {
  code: string;
  diagnosisDescription: string;
  microscopicDescription: string;
  categoryId: string;
  cptId: string;
  icdId: string;
}

interface RequestMacroModalProps {
  opened: boolean;
  onClose: () => void;
  categories: Array<{ value: string; label: string }>;
  cptCodes: Array<{ value: string; label: string }>;
  icdCodes: Array<{ value: string; label: string }>;
}

export default function RequestMacroModal({
  opened,
  onClose,
  categories,
  cptCodes,
  icdCodes,
}: RequestMacroModalProps) {
  const createRequest = useCreateMacroRequest();

  const form = useForm<RequestMacroFormValues>({
    initialValues: {
      code: "",
      diagnosisDescription: "",
      microscopicDescription: "",
      categoryId: "",
      cptId: "",
      icdId: "",
    },
    validate: {
      code: required("Diagnosis code is required"),
      diagnosisDescription: required("Diagnosis description is required"),
    },
  });

  const handleSubmit = async (values: RequestMacroFormValues) => {
    await createRequest.mutateAsync({
      code: values.code,
      diagnosisDescription: values.diagnosisDescription || undefined,
      microscopicDescription: values.microscopicDescription || undefined,
      categoryId: values.categoryId || undefined,
      cptId: values.cptId || undefined,
      icdId: values.icdId || undefined,
    });
    form.reset();
    onClose();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Request New Diagnosis Macro"
      size="lg"
      centered
      closeOnClickOutside={false}
      styles={{ body: { padding: 0 } }}
    >
      <form
        id="request-macro-form"
        noValidate
        onSubmit={form.onSubmit(handleSubmit, () => scrollToFirstError(form))}
      >
        <ScrollArea.Autosize
          mah="calc(100vh - 300px)"
          offsetScrollbars
          px="md"
          pt="md"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Submit a request for a new diagnosis macro. An administrator will
              review your request and, if approved, add it to the macro library.
            </Text>

            <TextInput
              label="Diagnosis Code"
              placeholder="Enter proposed diagnosis code"
              description="Short code pathologists use for quick diagnosis entry"
              withAsterisk
              {...form.getInputProps("code")}
            />

            <Select
              label="Diagnosis Category"
              placeholder="Select category"
              searchable
              clearable
              data={[...categories].sort((a, b) =>
                a.label.localeCompare(b.label)
              )}
              {...form.getInputProps("categoryId")}
            />

            <Textarea
              label="Diagnosis Description"
              placeholder="Enter the proposed diagnosis description text..."
              description="Use {{variable_name}} syntax for dynamic fields (e.g., {{source}}, {{collection_method}})"
              withAsterisk
              minRows={4}
              autosize
              {...form.getInputProps("diagnosisDescription")}
            />

            <Textarea
              label="Microscopic Description"
              placeholder="Enter microscopic description (optional)"
              description="Use {{variable_name}} syntax for dynamic fields"
              minRows={3}
              autosize
              {...form.getInputProps("microscopicDescription")}
            />

            <Group grow align="flex-start">
              <Select
                label="CPT Code"
                placeholder="Select CPT code"
                searchable
                clearable
                data={[...cptCodes].sort((a, b) =>
                  a.label.localeCompare(b.label)
                )}
                {...form.getInputProps("cptId")}
              />

              <Select
                label="ICD Code"
                placeholder="Select ICD code"
                searchable
                clearable
                data={[...icdCodes].sort((a, b) =>
                  a.label.localeCompare(b.label)
                )}
                {...form.getInputProps("icdId")}
              />
            </Group>
          </Stack>
        </ScrollArea.Autosize>

        <Box
          px="md"
          py="sm"
          style={{
            borderTop: "1px solid var(--mantine-color-default-border)",
          }}
        >
          <Group justify="space-between">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={createRequest.isPending}>
              Submit Request
            </Button>
          </Group>
        </Box>
      </form>
    </Modal>
  );
}
